import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import LeadMagnetLandingPage from './pages/LeadMagnetLandingPage';
import BookingLandingPage from './pages/BookingLandingPage';
import MealSamplePage from './pages/MealSamplePage';
import AlapozoPage from './pages/AlapozoPage';
import AnnouncementBar from './components/AnnouncementBar';
import BlogPostPage from './pages/BlogPostPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import FaqPage from './pages/FaqPage';
import ConfirmBookingPage from './pages/ConfirmBookingPage';
import AdminSlotsPage from './pages/AdminSlotsPage';
import AdminCompliancePage from './pages/AdminCompliancePage';
import AdminCustomOffersPage from './pages/AdminCustomOffersPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import PortalToolsPage from './pages/PortalToolsPage';
import AdminBlogAnalyticsPage from './pages/AdminBlogAnalyticsPage';
import CustomOfferPage from './pages/CustomOfferPage';
import LegacyBlogRedirect from './pages/LegacyBlogRedirect';
import AdminBlogPage from './pages/AdminBlogPage';
import AdminPromoPopupPage from './pages/AdminPromoPopupPage';
import AdminCouponsPage from './pages/AdminCouponsPage';
import AdminBankTransfersPage from './pages/AdminBankTransfersPage';
import AdminBookingsPage from './pages/AdminBookingsPage';
import AdminConsultationOrdersPage from './pages/AdminConsultationOrdersPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminLeadsPage from './pages/AdminLeadsPage';
import AdminTrainingPage from './pages/AdminTrainingPage';
import AdminTrainingPlanPrintPage from './pages/AdminTrainingPlanPrintPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminFeedbackPage from './pages/AdminFeedbackPage';
import AdminContactsPage from './pages/AdminContactsPage';
import AdminMealPlanEditorPage from './pages/AdminMealPlanEditorPage';
import AdminClientProgressPage from './pages/AdminClientProgressPage';
import AdminClientReportPage from './pages/AdminClientReportPage';
import PortalMealPlanPage from './pages/PortalMealPlanPage';
import TrainingPlanPrintPage from './pages/TrainingPlanPrintPage';
import AdminSmokeTestPage from './pages/AdminSmokeTestPage';
import PortalLoginPage from './pages/PortalLoginPage';
import PortalDashboardPage from './pages/PortalDashboardPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ImpresszumPage from './pages/ImpresszumPage';
import AszfPage from './pages/AszfPage';
import AdatvedelemPage from './pages/AdatvedelemPage';
import PaymentReturnPage from './pages/PaymentReturnPage';
import AnamnesisFormPage from './pages/AnamnesisFormPage';
import WithdrawalPage from './pages/WithdrawalPage';
import ProgramLandingPage from './pages/ProgramLandingPage';
import SzemelyiEdzoMiskolcPage from './pages/SzemelyiEdzoMiskolcPage';
import OnlineSzemelyiEdzoPage from './pages/OnlineSzemelyiEdzoPage';
import useBarionPageTracking from './utils/useBarionPageTracking';
import useGoogleAnalytics from './utils/useGoogleAnalytics';
import useMetaPixelPageTracking from './utils/useMetaPixelPageTracking';
import CookieConsentBanner from './components/CookieConsentBanner';
import ChatWidget from './components/ChatWidget';
import { markAdminVisit } from './utils/blogTracking';
import './App.css';
import { captureUtm } from './utils/utm';

const AppRoutes = () => {
  useBarionPageTracking();
  useGoogleAnalytics();
  useMetaPixelPageTracking();
  // Ha az admin felületen járt a látogató, a cikk-figyelő ebben a
  // sessionben már nem számolja a cikkmegnyitásokat.
  const { pathname } = useLocation();
  React.useEffect(() => {
    if (pathname.startsWith('/admin')) markAdminVisit();
    captureUtm();
  }, [pathname]);
  // A felső értesítő sáv csak a nyilvános oldalakon jelenik meg (az adminban,
  // az ügyfélfiókban és magán az /alapozo landingen nem).
  const hideBar = ['/admin', '/fiokom', '/alapozo', '/checkout', '/onboarding', '/egyedi-ajanlat']
    .some((p) => pathname.startsWith(p));

  return (
    <>
      {!hideBar && <AnnouncementBar />}
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/szemelyi-edzo-miskolc" element={<SzemelyiEdzoMiskolcPage />} />
      <Route path="/online-szemelyi-edzo" element={<OnlineSzemelyiEdzoPage />} />
      <Route path="/ingyenes-pdf" element={<LeadMagnetLandingPage />} />
      <Route path="/idopontfoglalas" element={<BookingLandingPage />} />
      <Route path="/foglalas" element={<Navigate to="/idopontfoglalas" replace />} />
      <Route path="/minta-etrend" element={<MealSamplePage />} />
      <Route path="/alapozo" element={<AlapozoPage />} />
      <Route path="/tudastar" element={<KnowledgeBasePage />} />
      <Route path="/gyik" element={<FaqPage />} />
      <Route path="/foglalas-megerositese" element={<ConfirmBookingPage />} />
      <Route path="/tudastar/:slug" element={<BlogPostPage />} />
      <Route path="/admin" element={<AdminDashboardPage />} />
      <Route path="/admin/calendar" element={<Navigate to="/admin/slots" replace />} />
      <Route path="/admin/slots" element={<AdminSlotsPage />} />
      <Route path="/admin/beallitasok" element={<AdminSettingsPage />} />
      <Route path="/admin/compliance" element={<AdminCompliancePage />} />
      <Route path="/admin/egyedi-ajanlatok" element={<AdminCustomOffersPage />} />
      <Route path="/admin/cikk-figyelo" element={<AdminBlogAnalyticsPage />} />
      <Route path="/egyedi-ajanlat/:token" element={<CustomOfferPage />} />
      <Route path="/admin/blog" element={<AdminBlogPage />} />
      <Route path="/admin/promo" element={<AdminPromoPopupPage />} />
      <Route path="/admin/kuponok" element={<AdminCouponsPage />} />
      <Route path="/admin/utalasok" element={<AdminBankTransfersPage />} />
      <Route path="/admin/foglalasok" element={<AdminBookingsPage />} />
      <Route path="/admin/konzultaciok" element={<AdminConsultationOrdersPage />} />
      <Route path="/admin/smoke-test" element={<AdminSmokeTestPage />} />
      <Route path="/admin/leadek" element={<AdminLeadsPage />} />
      <Route path="/admin/edzestervek" element={<AdminTrainingPage />} />
      <Route path="/admin/edzesterv/:planId" element={<AdminTrainingPlanPrintPage />} />
      <Route path="/admin/etrendtervek" element={<AdminMealPlanEditorPage />} />
      <Route path="/admin/haladas" element={<AdminClientProgressPage />} />
      <Route path="/admin/riport/:userId" element={<AdminClientReportPage />} />
      <Route path="/fiokom/etrendem" element={<PortalMealPlanPage />} />
      <Route path="/fiokom/naplo" element={<PortalToolsPage />} />
      <Route path="/admin/fiokok" element={<AdminUsersPage />} />
      <Route path="/admin/visszajelzesek" element={<AdminFeedbackPage />} />
      <Route path="/admin/kapcsolat" element={<AdminContactsPage />} />
      <Route path="/fiokom/edzesterv/:planId" element={<TrainingPlanPrintPage />} />
      <Route path="/fiokom" element={<PortalDashboardPage />} />
      <Route path="/fiokom/bejelentkezes" element={<PortalLoginPage />} />
      <Route path="/fiokom/jelszo-beallitas" element={<SetPasswordPage />} />
      <Route path="/adatkezelesi-tajekoztato" element={<AdatvedelemPage />} />
      <Route path="/adatvedelem" element={<AdatvedelemPage />} />
      <Route path="/impresszum" element={<ImpresszumPage />} />
      <Route path="/aszf" element={<AszfPage />} />
      <Route path="/elallasi-nyilatkozat" element={<WithdrawalPage />} />
      <Route path="/checkout/return" element={<PaymentReturnPage />} />
      <Route path="/onboarding/kerdoiv" element={<AnamnesisFormPage />} />
      {/* Ad-only landing pages — one program per URL, no cross-navigation.
          Slugs live in /config/programLandings.js so the coach can rename
          them without touching this router. */}
      <Route path="/eletmodvalto"       element={<ProgramLandingPage slug="eletmodvalto" />} />
      <Route path="/hipertrofia"        element={<ProgramLandingPage slug="hipertrofia" />} />
      <Route path="/kiscsoportos-edzes" element={<ProgramLandingPage slug="kiscsoportos-edzes" />} />
      <Route path="/premium-coaching"   element={<ProgramLandingPage slug="premium-coaching" />} />
      {/* Legacy blog URL redirect: `/{slug}` → `/tudastar/{slug}` when the
          slug matches a published post. Positioned before the `*` catch-all
          so React Router only reaches here for one-segment unknown paths. */}
      <Route path="/:legacySlug" element={<LegacyBlogRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <CookieConsentBanner />
      <ChatWidget />
    </BrowserRouter>
  );
}

export default App;
 App;
